import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Search, Calendar, Tag, ChevronRight } from 'lucide-react'
import { useBlogPosts, useBlogCategories, useBlogTags } from '../hooks/useBlog'
import { SEOHead } from '../components/SEOHead'
import { formatDate } from '../utils/helpers'
import { Breadcrumbs, generateBreadcrumbs } from '../components/Breadcrumbs'

export function BlogPage() {
  const location = useLocation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const { posts, loading, error } = useBlogPosts({
    page: currentPage,
    per_page: 12,
    category: selectedCategory || undefined,
    tag: selectedTag || undefined,
    search: searchTerm || undefined
  })

  const { categories } = useBlogCategories()
  const { tags } = useBlogTags()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1) // Reset to first page when searching
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
    setSelectedTag('')
    setCurrentPage(1)
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="card max-w-4xl mx-auto text-center py-16">
            <h2 className="text-2xl font-semibold text-red-600 mb-4">
              Error Loading Blog
            </h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <SEOHead
        title="AI SEO Blog - Latest Insights on llm.txt and AI Optimization"
        description="Stay updated with the latest AI SEO trends, llm.txt best practices, and insights on optimizing your website for AI tools like ChatGPT, Claude, and Gemini."
        keywords="AI SEO blog, llm.txt news, AI optimization tips, ChatGPT SEO, Claude SEO, AI tools, website optimization"
        canonical="https://llmtext.net/blog"
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <Breadcrumbs items={generateBreadcrumbs(location.pathname)} />
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="page-title">AI SEO Blog</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Latest insights, tips, and news about llm.txt and AI SEO.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="card max-w-4xl mx-auto">
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </form>

            <div className="flex flex-wrap gap-4 mb-4">
              {/* Category Filter */}
              <div>
                <label className="label">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input-field"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div>
                <label className="label">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="input-field"
                >
                  <option value="">All Tags</option>
                  {tags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(searchTerm || selectedCategory || selectedTag) && (
              <button
                onClick={clearFilters}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>

        {/* Blog Posts */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="card max-w-4xl mx-auto text-center py-16">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              No Posts Found
            </h2>
            <p className="text-gray-600">
              {searchTerm || selectedCategory || selectedTag
                ? 'Try adjusting your search criteria or clearing filters.'
                : 'No blog posts have been published yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article key={post.id} className="card hover:shadow-xl transition-shadow duration-300">
                {post.featured && (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-primary text-white">
                      Featured
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="hover:text-primary-600 transition-colors duration-200"
                    >
                      {post.title}
                    </Link>
                  </h2>

                  <p className="text-gray-600 text-sm line-clamp-3">
                    {post.excerpt}
                  </p>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(post.created_at)}
                    </div>
                    {post.category && (
                      <div className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        {post.category}
                      </div>
                    )}
                  </div>
                </div>

                {post.tags && post.tags.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{post.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors duration-200"
                >
                  Read more
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  )
}
